import { ReactNode, useMemo } from 'react';
import Icon from 'src/components/Icon';

type SortButtonProps = {
  tag: keyof HTMLElementTagNameMap;
  sortKey: string;
  sortBy: string;
  setSortBy: Function;
  style?: Record<string, string | number>;
  children: ReactNode;
};

const chevrons = { asc: 'fa-sort-alpha-down', desc: 'fa-sort-alpha-up', none: 'fa-sort' };
const iconStyles = { height: '100%', alignItems: 'center', display: 'flex' };

function SortButton({ tag = 'button', sortKey, sortBy, setSortBy, style = {}, children }: SortButtonProps) {
  const Tag = tag;

  const defaultsToDesc = useMemo(() => sortKey.startsWith('-'), [sortKey]);

  // Base key is always without the minus
  const baseKey = useMemo(() => (defaultsToDesc ? sortKey.slice(1) : sortKey), [sortKey, defaultsToDesc]);

  const ascSortKey = baseKey;
  const descSortKey = `-${baseKey}`;

  const sorting = useMemo(() => (sortBy === ascSortKey ? 'asc' : sortBy === descSortKey ? 'desc' : null), [sortBy, ascSortKey, descSortKey]);

  const chevron = useMemo(() => {
    if (defaultsToDesc && sorting === 'desc') return chevrons.asc;
    if (defaultsToDesc && sorting === 'asc') return chevrons.desc;
    return chevrons[sorting ?? 'none'];
  }, [sorting, defaultsToDesc]);

  const handleClick = () => {
    if (defaultsToDesc) {
      // none -> desc -> asc -> none
      setSortBy(sorting === null ? descSortKey : sorting === 'desc' ? ascSortKey : null);
    } else {
      // none -> asc -> desc -> none
      setSortBy(sorting === null ? ascSortKey : sorting === 'asc' ? descSortKey : null);
    }
  };

  return (
    <Tag className="sort-button animated-show" style={{ ...style, cursor: 'pointer' }} onClick={handleClick}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <div style={iconStyles}>{children}</div>{' '}
        {chevron ? (
          <div style={iconStyles}>
            <Icon icon={chevron} size={10} />
          </div>
        ) : null}
      </div>
    </Tag>
  );
}

export default SortButton;
